import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { navigate } from '@reach/router';
import Dropzone from 'react-dropzone';
import { toast } from 'react-toastify';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  formatTaxDisplay,
  getUnitPriceTaxTotal,
  normalizeTaxType,
  TAX_TYPE_AMOUNT,
  TAX_TYPE_PERCENT,
} from '../../utils/nonRxTax';
import PriceTaxTotalSummary from './PriceTaxTotalSummary';
import {
  fetchOtcDeliveryEnabled,
  getOtcLockedRedirectPath,
} from '../../utils/productLicensing';
import ILocalAGGrid, {
  getDefaultColDef,
  createServerSideDatasource,
  foldFiltersToSearchText,
  getActionsColumn,
  openAgGridContextMenu,
  actionMenuItem,
} from '../grid/iLocalAGGrid';
import './NonRxCatalog.css';

const filterObj = {
  pageNo: 0,
  pageSize: 10,
  status: 'active',
};
const UOM_OPTIONS = [
  { code: 'EA', labelKey: 'NonRxCatalog.Uom.EA', defaultLabel: 'Each' },
  { code: 'PC', labelKey: 'NonRxCatalog.Uom.PC', defaultLabel: 'Piece' },
  { code: 'BX', labelKey: 'NonRxCatalog.Uom.BX', defaultLabel: 'Box' },
  { code: 'PK', labelKey: 'NonRxCatalog.Uom.PK', defaultLabel: 'Pack' },
  { code: 'CS', labelKey: 'NonRxCatalog.Uom.CS', defaultLabel: 'Case' },
  { code: 'BT', labelKey: 'NonRxCatalog.Uom.BT', defaultLabel: 'Bottle' },
  { code: 'TB', labelKey: 'NonRxCatalog.Uom.TB', defaultLabel: 'Tablet' },
];

const getUomFullLabel = (t, code) => {
  const option = UOM_OPTIONS.find((item) => item.code === code);
  if (!option) return code || '';
  return t(option.labelKey, { defaultValue: option.defaultLabel });
};

const formatUomDisplay = (t, code) => {
  if (!code) return '';
  return `${code} — ${getUomFullLabel(t, code)}`;
};
const PLACEHOLDER_IMAGE =
  'https://s3.amazonaws.com/rpx-dev-ext-images-qr/NoImageFound.png';

const emptyForm = {
  id: '',
  upc: '',
  item_number: '',
  description: '',
  sell_price: '',
  uom: 'EA',
  is_taxable: false,
  tax_type: TAX_TYPE_AMOUNT,
  tax_amount: '',
  status: 'active',
  image_url: '',
};

const getAuthToken = () => {
  const user = JSON.parse(sessionStorage.getItem('user') || 'null');
  return user && user.signInUserSession && user.signInUserSession.idToken
    ? user.signInUserSession.idToken.jwtToken
    : '';
};

const getAccountId = () => sessionStorage.getItem('parentAccountId') || '';

const formatMoney = (value) => {
  const parsed = parseFloat(value);
  return `$${(Number.isNaN(parsed) ? 0 : parsed).toFixed(2)}`;
};

const NonRxCatalog = ({ t, onViewChange, defaultTitle }) => {
  const [mode, setMode] = useState('list');
  const [otcAllowed, setOtcAllowed] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [errors, setErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [statusFilter, setStatusFilter] = useState(filterObj.status || 'active');
  const gridApiRef = useRef(null);
  const defaultColDefRef = useRef(getDefaultColDef());

  useEffect(() => {
    let cancelled = false;
    const gate = async () => {
      const enabled = await fetchOtcDeliveryEnabled();
      if (cancelled) {
        return;
      }
      if (!enabled) {
        navigate(getOtcLockedRedirectPath(), { replace: true });
        return;
      }
      setOtcAllowed(true);
    };
    gate();
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshGrid = useCallback(() => {
    const api = gridApiRef.current;
    if (!api) return;
    if (typeof api.refreshServerSideStore === 'function') {
      api.refreshServerSideStore({ purge: true });
    } else if (typeof api.refreshServerSide === 'function') {
      api.refreshServerSide({ purge: true });
    }
  }, []);

  const handleStatusFilterChange = (value) => {
    const nextStatus = value || 'active';
    filterObj.status = nextStatus;
    setStatusFilter(nextStatus);
    refreshGrid();
  };

  const onPaginationChanged = useCallback(() => {
    const api = gridApiRef.current;
    if (!api) return;
    const newPageSize = api.paginationGetPageSize();
    if (newPageSize && filterObj.pageSize !== newPageSize) {
      filterObj.pageSize = newPageSize;
    }
  }, []);

  const onGridReady = useCallback(
    (params) => {
      gridApiRef.current = params.api;
      const datasource = createServerSideDatasource({
        filterBody: filterObj,
        applyFilterModel: (body, filterModel) => {
          const searchText = foldFiltersToSearchText(filterModel);
          body.search = searchText;
          body.searchtext = searchText;
        },
        pageZeroBased: true,
        getPageSize: () => filterObj.pageSize || 10,
        fetchPage: (body) =>
          fetch(`${process.env.REACT_APP_BASEURL}api/non-rx-products`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${getAuthToken()}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              search: body.search || body.searchtext || '',
              page: body.pageNo,
              pageSize: body.pageSize,
              account_id: getAccountId(),
              status: filterObj.status || 'active',
            }),
          }).then((response) => response.json()),
        onError: (error) => {
          const message =
            (error && error.message) || 'Failed to load catalog.';
          toast.error(message);
        },
      });
      if (typeof params.api.setServerSideDatasource === 'function') {
        params.api.setServerSideDatasource(datasource);
      }
    },
    []
  );

  const openCreate = () => {
    setForm(emptyForm);
    setImageFile(null);
    setImagePreview('');
    setErrors({});
    setMode('create');
  };

  const openEdit = useCallback((product) => {
    setForm({
      id: product.id,
      upc: product.upc || '',
      item_number: product.item_number || '',
      description: product.description || '',
      sell_price: product.sell_price != null ? product.sell_price : '',
      uom: product.uom || 'EA',
      is_taxable: product.is_taxable === true || product.is_taxable === 'true',
      tax_type: normalizeTaxType(product.tax_type),
      tax_amount: product.tax_amount != null ? product.tax_amount : '',
      status: product.status || 'active',
      image_url: product.image_url || '',
    });
    setImageFile(null);
    setImagePreview(product.image_url || '');
    setErrors({});
    setMode('edit');
  }, []);

  const getContextMenuItems = useCallback(
    (params) => {
      const colId =
        params &&
        params.column &&
        (params.column.getColId
          ? params.column.getColId()
          : params.column.colId);
      if (colId !== 'actions') return [];
      const product = params.node && params.node.data;
      if (!product) return [];
      return [
        actionMenuItem({
          name: t('Common.Edit', { defaultValue: 'Edit' }),
          icon: 'edit',
          action: () => openEdit(product),
        }),
        actionMenuItem({
          name: t('NonRxCatalog.Delete', { defaultValue: 'Delete' }),
          icon: 'delete',
          action: () => setDeleteTarget(product),
        }),
      ];
    },
    [openEdit, t]
  );

  const openRowActionsMenu = useCallback(
    (event, params, menuHelpers) => {
      const openMenu = () => {
        const items = getContextMenuItems({
          ...params,
          column: params.column,
          node: params.node,
          value: params.value,
        });
        if (menuHelpers && typeof menuHelpers.openMenu === 'function') {
          menuHelpers.openMenu(items);
          return;
        }
        openAgGridContextMenu(
          params,
          (menuHelpers && menuHelpers.positionEvent) || event
        );
      };
      openMenu();
    },
    [getContextMenuItems]
  );

  const columnDefs = useMemo(
    () => [
      {
        field: 'image_url',
        headerName: t('NonRxCatalog.Image', { defaultValue: 'Image' }),
        sortable: false,
        filter: false,
        maxWidth: 80,
        minWidth: 72,
        flex: 0,
        cellRenderer: (params) => {
          const src =
            (params.data && params.data.image_url) || PLACEHOLDER_IMAGE;
          return (
            <img
              src={src}
              alt=""
              style={{
                width: 36,
                height: 36,
                objectFit: 'contain',
                display: 'block',
                margin: '0 auto',
              }}
            />
          );
        },
      },
      {
        field: 'upc',
        headerName: t('NonRxCatalog.Upc', { defaultValue: 'UPC' }),
        minWidth: 140,
      },
      {
        field: 'item_number',
        headerName: t('NonRxCatalog.ItemNumber', { defaultValue: 'Item #' }),
        minWidth: 120,
      },
      {
        field: 'description',
        headerName: t('NonRxCatalog.Description', {
          defaultValue: 'Description',
        }),
        minWidth: 200,
      },
      {
        field: 'uom',
        headerName: t('NonRxCatalog.Uom', { defaultValue: 'UOM' }),
        maxWidth: 160,
        minWidth: 130,
        flex: 0,
        valueFormatter: (params) => formatUomDisplay(t, params.value),
      },
      {
        field: 'sell_price',
        headerName: t('NonRxCatalog.SellPrice', { defaultValue: 'Sell Price' }),
        maxWidth: 120,
        minWidth: 110,
        flex: 0,
        filter: false,
        valueFormatter: (params) => formatMoney(params.value),
      },
      {
        colId: 'tax_display',
        headerName: t('NonRxCatalog.Tax', { defaultValue: 'Tax' }),
        maxWidth: 150,
        minWidth: 120,
        flex: 0,
        filter: false,
        sortable: false,
        valueGetter: (params) => {
          const row = params.data;
          if (!row) return '';
          if (row.is_taxable) {
            return formatTaxDisplay(row);
          }
          return t('NonRxCatalog.NonTaxable', { defaultValue: 'Non-taxable' });
        },
      },
      {
        field: 'status',
        headerName: t('NonRxCatalog.Status', { defaultValue: 'Status' }),
        maxWidth: 110,
        minWidth: 100,
        flex: 0,
        valueFormatter: (params) => {
          if (params.value === 'inactive') {
            return t('NonRxCatalog.Inactive', { defaultValue: 'Inactive' });
          }
          return t('NonRxCatalog.Active', { defaultValue: 'Active' });
        },
      },
      getActionsColumn({
        headerName: t('Users.Actions', { defaultValue: 'Actions' }),
        onActionsClick: (event, params, menuHelpers) =>
          openRowActionsMenu(event, params, menuHelpers),
      }),
    ],
    [openRowActionsMenu, t]
  );

  const handleFormChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'is_taxable' && !value) {
        next.tax_amount = '';
        next.tax_type = TAX_TYPE_AMOUNT;
      }
      return next;
    });
  };

  const handleDrop = (files) => {
    const file = files && files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const validate = () => {
    const nextErrors = {};
    if (!String(form.upc || '').trim()) {
      nextErrors.upc = t('NonRxCatalog.UpcRequired', {
        defaultValue: 'UPC is required.',
      });
    }
    if (!String(form.item_number || '').trim()) {
      nextErrors.item_number = t('NonRxCatalog.ItemNumberRequired', {
        defaultValue: 'Item # is required.',
      });
    }
    if (!String(form.description || '').trim()) {
      nextErrors.description = t('NonRxCatalog.DescriptionRequired', {
        defaultValue: 'Description is required.',
      });
    }
    const price = parseFloat(form.sell_price);
    if (form.sell_price === '' || Number.isNaN(price) || price < 0) {
      nextErrors.sell_price = t('NonRxCatalog.PriceInvalid', {
        defaultValue: 'Sell price must be 0 or greater.',
      });
    }
    if (form.is_taxable) {
      const tax = parseFloat(form.tax_amount);
      const taxType = normalizeTaxType(form.tax_type);
      if (form.tax_amount === '' || Number.isNaN(tax) || tax < 0) {
        nextErrors.tax_amount = t('NonRxCatalog.TaxInvalid', {
          defaultValue: 'Tax is required when taxable.',
        });
      } else if (taxType === TAX_TYPE_PERCENT && tax > 100) {
        nextErrors.tax_amount = t('NonRxCatalog.TaxPercentInvalid', {
          defaultValue: 'Tax percent must be between 0 and 100.',
        });
      }
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    const accountId = getAccountId();
    if (!accountId) {
      toast.error(
        t('NonRxCatalog.AccountRequired', {
          defaultValue: 'Account is required.',
        })
      );
      return;
    }
    setSaving(true);
    try {
      const data = new FormData();
      data.append('account_id', accountId);
      data.append('upc', String(form.upc).replace(/\s+/g, '').trim());
      data.append('item_number', String(form.item_number).trim());
      data.append('description', String(form.description).trim());
      data.append('sell_price', form.sell_price);
      data.append('uom', form.uom || 'EA');
      data.append('is_taxable', form.is_taxable ? 'true' : 'false');
      data.append(
        'tax_type',
        form.is_taxable ? normalizeTaxType(form.tax_type) : TAX_TYPE_AMOUNT
      );
      data.append('tax_amount', form.is_taxable ? form.tax_amount || 0 : 0);
      data.append('status', form.status || 'active');
      if (form.id) {
        data.append('id', form.id);
        if (form.image_url && !imageFile) {
          data.append('image_url', form.image_url);
        }
      }
      if (imageFile) {
        data.append('file', imageFile);
      }
      const isEdit = mode === 'edit';
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}api/non-rx-products/${
          isEdit ? 'update' : 'save'
        }`,
        {
          method: isEdit ? 'PUT' : 'POST',
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body: data,
        }
      );
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || 'Save failed');
      }
      toast.success(
        t(isEdit ? 'NonRxCatalog.Updated' : 'NonRxCatalog.Created', {
          defaultValue: isEdit
            ? 'Product updated successfully.'
            : 'Product added successfully.',
        })
      );
      setMode('list');
    } catch (error) {
      toast.error(error.message || 'Failed to save product.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}api/non-rx-products/delete/${deleteTarget.id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
          },
        }
      );
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.message || 'Delete failed');
      }
      toast.success(
        t('NonRxCatalog.Deleted', { defaultValue: 'Product deleted.' })
      );
      setDeleteTarget(null);
      refreshGrid();
    } catch (error) {
      toast.error(error.message || 'Failed to delete product.');
    } finally {
      setSaving(false);
    }
  };

  const heading = useMemo(() => {
    if (mode === 'create') {
      return t('NonRxCatalog.AddTitle', { defaultValue: 'Add Marketplace product' });
    }
    if (mode === 'edit') {
      return t('NonRxCatalog.EditTitle', {
        defaultValue: 'Edit Marketplace product',
      });
    }
    return defaultTitle || t('Leftbar.NonRxCatalog', { defaultValue: 'Marketplace' });
  }, [defaultTitle, mode, t]);

  useEffect(() => {
    if (typeof onViewChange !== 'function') {
      return;
    }
    if (otcAllowed !== true) {
      onViewChange({
        title: heading,
        isListView: false,
      });
      return;
    }
    onViewChange({
      title: heading,
      isListView: mode === 'list',
    });
  }, [heading, mode, onViewChange, otcAllowed]);

  if (otcAllowed !== true) {
    return (
      <div className="non-rx-catalog-page non-rx-catalog-loading">
        <CircularProgress size={28} />
      </div>
    );
  }

  if (mode !== 'list') {
    const priceTaxTotal = getUnitPriceTaxTotal(form);
    const fieldProps = {
      fullWidth: true,
      margin: 'dense',
      size: 'small',
      variant: 'outlined',
      className: 'non-rx-catalog-input',
    };

    return (
      <div className="non-rx-catalog-page non-rx-catalog-form-page">
        <Box className="non-rx-catalog-form-panel">
          <header className="non-rx-catalog-form-topbar">
            <div className="non-rx-catalog-form-topbar-text">
              <Typography component="h1" className="non-rx-catalog-form-topbar-title">
                {heading}
              </Typography>
              <Typography component="p" className="non-rx-catalog-form-topbar-subtitle">
                {t('NonRxCatalog.FormHint', {
                  defaultValue:
                    'Enter product details for Marketplace items available at pickup.',
                })}
              </Typography>
            </div>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => setMode('list')}
              className="non-rx-catalog-back-btn"
            >
              {t('NonRxCatalog.Back', { defaultValue: 'Back to catalog' })}
            </Button>
          </header>

          <div className="non-rx-catalog-form-layout">
            <section className="non-rx-catalog-photo-section" aria-label="Product photo">
              <Typography variant="subtitle2" className="non-rx-catalog-section-label">
                {t('NonRxCatalog.ProductPhoto', { defaultValue: 'Product photo' })}
                <span className="non-rx-catalog-optional-label">
                  {t('NonRxCatalog.Optional', { defaultValue: '(optional)' })}
                </span>
              </Typography>
              <Dropzone
                accept="image/jpeg,image/png,image/jpg,image/webp"
                multiple={false}
                onDrop={handleDrop}
                className={`non-rx-catalog-dropzone${
                  imagePreview ? ' non-rx-catalog-dropzone--filled' : ''
                }`}
                activeClassName="non-rx-catalog-dropzone--active"
              >
                {imagePreview ? (
                  <React.Fragment>
                    <img src={imagePreview} alt="" />
                    <span className="non-rx-catalog-dropzone-overlay">
                      {t('NonRxCatalog.ChangePhoto', {
                        defaultValue: 'Click or drop to replace',
                      })}
                    </span>
                  </React.Fragment>
                ) : (
                  <div className="non-rx-catalog-dropzone-placeholder">
                    <CloudUploadOutlinedIcon className="non-rx-catalog-dropzone-icon" />
                    <Typography variant="body2" className="non-rx-catalog-dropzone-text">
                      {t('NonRxCatalog.DropImage', {
                        defaultValue: 'Drop photo or click to upload',
                      })}
                    </Typography>
                    <Typography variant="caption" className="non-rx-catalog-dropzone-hint">
                      JPG, PNG, WEBP
                    </Typography>
                  </div>
                )}
              </Dropzone>
            </section>

            <section className="non-rx-catalog-fields-section">
              <Typography variant="subtitle2" className="non-rx-catalog-section-label">
                {t('NonRxCatalog.ProductInfo', {
                  defaultValue: 'Product information',
                })}
              </Typography>
              <div className="non-rx-catalog-field-grid">
                <div className="non-rx-catalog-field non-rx-catalog-field--half">
                  <TextField
                    {...fieldProps}
                    required
                    label={t('NonRxCatalog.Upc', { defaultValue: 'UPC' })}
                    value={form.upc}
                    onChange={(event) => handleFormChange('upc', event.target.value)}
                    error={!!errors.upc}
                    helperText={
                      errors.upc ||
                      t('NonRxCatalog.UpcHint', {
                        defaultValue:
                          'Barcode for scanning on Deliver. Use the UPC printed on the product.',
                      })
                    }
                    inputProps={{ maxLength: 32 }}
                  />
                </div>
                <div className="non-rx-catalog-field non-rx-catalog-field--half">
                  <TextField
                    {...fieldProps}
                    required
                    label={t('NonRxCatalog.ItemNumber', { defaultValue: 'Item #' })}
                    value={form.item_number}
                    onChange={(event) =>
                      handleFormChange('item_number', event.target.value)
                    }
                    error={!!errors.item_number}
                    helperText={errors.item_number}
                    inputProps={{ maxLength: 100 }}
                  />
                </div>
                <div className="non-rx-catalog-field non-rx-catalog-field--full">
                  <TextField
                    {...fieldProps}
                    required
                    label={t('NonRxCatalog.Description', {
                      defaultValue: 'Description',
                    })}
                    value={form.description}
                    onChange={(event) =>
                      handleFormChange('description', event.target.value)
                    }
                    error={!!errors.description}
                    helperText={errors.description}
                    inputProps={{ maxLength: 255 }}
                  />
                </div>
              </div>

              <Typography variant="subtitle2" className="non-rx-catalog-section-label">
                {t('NonRxCatalog.PricingTax', {
                  defaultValue: 'Pricing & tax',
                })}
              </Typography>
              <div className="non-rx-catalog-field-grid">
                <div className="non-rx-catalog-field non-rx-catalog-field--third">
                  <TextField
                    {...fieldProps}
                    required
                    type="number"
                    label={t('NonRxCatalog.SellPrice', {
                      defaultValue: 'Sell Price',
                    })}
                    value={form.sell_price}
                    onChange={(event) =>
                      handleFormChange('sell_price', event.target.value)
                    }
                    error={!!errors.sell_price}
                    helperText={errors.sell_price}
                    inputProps={{ min: 0, step: '0.01' }}
                  />
                </div>
                <div className="non-rx-catalog-field non-rx-catalog-field--third">
                  <FormControl {...fieldProps}>
                    <InputLabel id="non-rx-uom-label">
                      {t('NonRxCatalog.Uom', { defaultValue: 'Unit of Measure' })}
                    </InputLabel>
                    <Select
                      labelId="non-rx-uom-label"
                      value={form.uom}
                      label={t('NonRxCatalog.Uom', {
                        defaultValue: 'Unit of Measure',
                      })}
                      onChange={(event) =>
                        handleFormChange('uom', event.target.value)
                      }
                    >
                      {UOM_OPTIONS.map((option) => (
                        <MenuItem key={option.code} value={option.code}>
                          {formatUomDisplay(t, option.code)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                <div className="non-rx-catalog-field non-rx-catalog-field--third">
                  <FormControl {...fieldProps}>
                    <InputLabel id="non-rx-status-label">
                      {t('NonRxCatalog.Status', { defaultValue: 'Status' })}
                    </InputLabel>
                    <Select
                      labelId="non-rx-status-label"
                      value={form.status}
                      label={t('NonRxCatalog.Status', { defaultValue: 'Status' })}
                      onChange={(event) =>
                        handleFormChange('status', event.target.value)
                      }
                    >
                      <MenuItem value="active">
                        {t('NonRxCatalog.Active', { defaultValue: 'Active' })}
                      </MenuItem>
                      <MenuItem value="inactive">
                        {t('NonRxCatalog.Inactive', { defaultValue: 'Inactive' })}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </div>
                <div className="non-rx-catalog-field non-rx-catalog-field--full">
                  <Box className="non-rx-catalog-tax-card">
                    <FormControlLabel
                      className="non-rx-catalog-taxable-toggle"
                      control={
                        <Switch
                          checked={!!form.is_taxable}
                          onChange={(event) =>
                            handleFormChange('is_taxable', event.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label={t('NonRxCatalog.Taxable', { defaultValue: 'Taxable' })}
                    />
                    <FormControl
                      className="non-rx-catalog-tax-type"
                      disabled={!form.is_taxable}
                    >
                      <RadioGroup
                        row
                        value={normalizeTaxType(form.tax_type)}
                        onChange={(event) =>
                          handleFormChange('tax_type', event.target.value)
                        }
                      >
                        <FormControlLabel
                          value={TAX_TYPE_AMOUNT}
                          control={<Radio size="small" />}
                          label={t('NonRxCatalog.TaxTypeAmount', {
                            defaultValue: 'Amount ($)',
                          })}
                        />
                        <FormControlLabel
                          value={TAX_TYPE_PERCENT}
                          control={<Radio size="small" />}
                          label={t('NonRxCatalog.TaxTypePercent', {
                            defaultValue: 'Percent (%)',
                          })}
                        />
                      </RadioGroup>
                    </FormControl>
                    <TextField
                      {...fieldProps}
                      className="non-rx-catalog-input non-rx-catalog-tax-amount"
                      type="number"
                      disabled={!form.is_taxable}
                      label={
                        normalizeTaxType(form.tax_type) === TAX_TYPE_PERCENT
                          ? t('NonRxCatalog.TaxPercent', {
                              defaultValue: 'Tax percent',
                            })
                          : t('NonRxCatalog.TaxAmount', {
                              defaultValue: 'Tax amount',
                            })
                      }
                      value={form.tax_amount}
                      onChange={(event) =>
                        handleFormChange('tax_amount', event.target.value)
                      }
                      error={!!errors.tax_amount}
                      helperText={
                        errors.tax_amount ||
                        (normalizeTaxType(form.tax_type) === TAX_TYPE_PERCENT
                          ? t('NonRxCatalog.TaxPercentHint', {
                              defaultValue: 'Percent of sell price, per unit',
                            })
                          : t('NonRxCatalog.TaxHint', {
                              defaultValue: 'Fixed amount per unit',
                            }))
                      }
                      InputProps={{
                        startAdornment:
                          form.is_taxable &&
                          normalizeTaxType(form.tax_type) === TAX_TYPE_AMOUNT ? (
                            <InputAdornment position="start">$</InputAdornment>
                          ) : undefined,
                        endAdornment:
                          form.is_taxable &&
                          normalizeTaxType(form.tax_type) === TAX_TYPE_PERCENT ? (
                            <InputAdornment position="end">%</InputAdornment>
                          ) : undefined,
                        inputProps: {
                          min: 0,
                          max:
                            normalizeTaxType(form.tax_type) === TAX_TYPE_PERCENT
                              ? 100
                              : undefined,
                          step: '0.01',
                        },
                      }}
                    />
                    {form.is_taxable ? (
                      <PriceTaxTotalSummary
                        t={t}
                        price={priceTaxTotal.price}
                        tax={priceTaxTotal.tax}
                        isPercent={priceTaxTotal.isPercent}
                        showEach
                      />
                    ) : null}
                  </Box>
                </div>
              </div>
            </section>
          </div>

          <Box className="non-rx-catalog-form-actions">
            <Button
              onClick={() => setMode('list')}
              className="non-rx-catalog-btn-cancel"
            >
              {t('Common.Cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={saving}
              onClick={handleSave}
              className="non-rx-catalog-btn-save"
            >
              {saving
                ? t('NonRxCatalog.Saving', { defaultValue: 'Saving...' })
                : t('NonRxCatalog.Save', { defaultValue: 'Save product' })}
            </Button>
          </Box>
        </Box>
      </div>
    );
  }

  return (
    <div className="non-rx-catalog-page non-rx-catalog-list-page">
      <Box className="non-rx-catalog-toolbar">
        <Typography variant="body2" className="non-rx-catalog-toolbar-hint">
          {t('NonRxCatalog.ListHint', {
            defaultValue: 'Manage Marketplace products for pickup and delivery.',
          })}
        </Typography>
        <div className="non-rx-catalog-toolbar-actions">
          <FormControl
            size="small"
            variant="outlined"
            className="non-rx-catalog-status-filter"
          >
            <InputLabel id="non-rx-status-filter-label">
              {t('NonRxCatalog.Status', { defaultValue: 'Status' })}
            </InputLabel>
            <Select
              labelId="non-rx-status-filter-label"
              value={statusFilter}
              label={t('NonRxCatalog.Status', { defaultValue: 'Status' })}
              onChange={(event) => handleStatusFilterChange(event.target.value)}
            >
              <MenuItem value="active">
                {t('NonRxCatalog.Active', { defaultValue: 'Active' })}
              </MenuItem>
              <MenuItem value="inactive">
                {t('NonRxCatalog.Inactive', { defaultValue: 'Inactive' })}
              </MenuItem>
              <MenuItem value="all">
                {t('NonRxCatalog.AllStatuses', { defaultValue: 'All' })}
              </MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddCircleIcon />}
            onClick={openCreate}
            className="non-rx-catalog-btn-add"
          >
            {t('NonRxCatalog.AddProduct', { defaultValue: 'Add product' })}
          </Button>
        </div>
      </Box>
      <div className="non-rx-catalog-grid-wrap">
        <Divider className="non-rx-catalog-grid-divider" />
        <ILocalAGGrid
          columnDefs={columnDefs}
          defaultColDef={defaultColDefRef.current}
          rowModelType="serverSide"
          cacheBlockSize={filterObj.pageSize || 10}
          paginationPageSize={filterObj.pageSize || 10}
          paginationPageSizeSelector={[10, 20, 25]}
          rowHeight={52}
          getRowId={(params) =>
            (params.data && (params.data.__ssrmId || params.data.id)) || ''
          }
          onGridReady={onGridReady}
          onPaginationChanged={onPaginationChanged}
          getContextMenuItems={getContextMenuItems}
          overlayNoRowsMessage={t('NonRxCatalog.Empty', {
            defaultValue: 'No products found',
          })}
        />
      </div>
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>
          {t('NonRxCatalog.DeleteTitle', {
            defaultValue: 'Delete this product?',
          })}
        </DialogTitle>
        <DialogContent>
          {deleteTarget && deleteTarget.description}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>
            {t('Common.Cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button color="secondary" disabled={saving} onClick={handleDelete}>
            {t('NonRxCatalog.Delete', { defaultValue: 'Delete' })}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default withTranslation()(NonRxCatalog);



// WEBPACK FOOTER //
// ./src/components/non-rx-catalog/index.js